import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  afterRenderEffect,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { IconMinusSignComponent, IconPlusSignComponent } from '@pedix-workspace/angular-ui-icons';

export type CounterSize = 'sm' | 'md' | 'xl';
export type CounterColor = 'yellow' | 'orange' | 'blue' | 'cyan';

@Component({
  selector: 'pxw-counter',
  templateUrl: './counter.component.html',
  styleUrls: ['./counter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconMinusSignComponent, IconPlusSignComponent],
})
export class CounterComponent {
  readonly size = input<CounterSize>('xl');
  readonly color = input<CounterColor>('orange');
  readonly inverted = input(false);
  readonly count = input(1);
  readonly min = input(0);
  readonly max = input<number | undefined>(undefined);
  readonly disabled = input(false);
  readonly decrementDisabled = input(false);
  readonly incrementDisabled = input(false);

  readonly countChange = output<number>();

  private readonly countInputRef = viewChild<ElementRef<HTMLInputElement>>('countInput');
  private readonly isFocused = signal(false);

  readonly iconSize = computed(() => {
    switch (this.size()) {
      case 'sm':
        return 'xxs';
      default:
        return 'sm';
    }
  });

  readonly decrementIsDisabled = computed(
    () => this.disabled() || this.decrementDisabled() || this.count() <= this.floor(),
  );
  readonly incrementIsDisabled = computed(() => {
    const max = this.max();

    return (
      this.disabled() || this.incrementDisabled() || (max !== undefined && this.count() >= max)
    );
  });

  @HostBinding('class') get classes() {
    return [
      this.size(),
      this.color(),
      this.inverted() ? 'inverted' : '',
      this.disabled() ? 'disabled' : '',
    ].join(' ');
  }

  constructor() {
    afterRenderEffect(() => {
      const value = this.count();
      const input = this.countInputRef()?.nativeElement;

      if (input && !this.isFocused()) {
        input.value = String(value);
      }
    });
  }

  increment($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.incrementIsDisabled()) {
      return;
    }
    this.countChange.emit(this.count() + 1);
  }

  decrement($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.decrementIsDisabled()) {
      return;
    }
    this.countChange.emit(this.count() - 1);
  }

  onInput($event: Event) {
    const input = $event.target as HTMLInputElement;
    const nextCount = this.clampToDisabledCaps(this.clamp(parseInt(input.value, 10)));

    this.countChange.emit(nextCount);
  }

  onFocus() {
    this.isFocused.set(true);
  }

  onBlur() {
    this.isFocused.set(false);
  }

  onWheel($event: WheelEvent) {
    ($event.target as HTMLElement).blur();
  }

  onCounterClick($event: Event) {
    $event.stopPropagation();
  }

  private clampToDisabledCaps(value: number): number {
    if (this.incrementDisabled() && value > this.count()) {
      return this.count();
    }
    if (this.decrementDisabled() && value < this.count()) {
      return this.count();
    }
    return value;
  }

  private floor(): number {
    return Math.max(this.min(), 0);
  }

  private clamp(value: number): number {
    const floor = this.floor();
    const max = this.max();
    let nextCount = Number.isNaN(value) ? floor : value;

    if (nextCount < floor) {
      nextCount = floor;
    }
    if (max !== undefined && nextCount > max) {
      nextCount = max;
    }
    return nextCount;
  }
}
